import { useTranslation } from 'react-i18next';
import { Button, cn } from '@kidshome/ui';
import { Illustration } from '@/shared/ui/Illustration';
import { Spinner } from '@/shared/ui/icons';

/**
 * Shared async-state surfaces (loading / error / empty) reused by BOTH the family and admin shells.
 * Copy is kind and routed through the shared `common` namespace (so neither shell depends on the
 * other's catalog); the error state offers a retry. Each is presentational — the screen owns the data
 * and passes `onRetry`.
 */

export function LoadingState({ className }: { className?: string }): React.ReactElement {
  const { t } = useTranslation('common');
  return (
    <div
      className={cn('flex items-center justify-center gap-2 p-8 text-text-muted', className)}
      role="status"
      aria-live="polite"
    >
      <Spinner size={18} />
      <span>{t('states.loading')}</span>
    </div>
  );
}

export function ErrorState({
  onRetry,
  className,
}: {
  onRetry?: () => void;
  className?: string;
}): React.ReactElement {
  const { t } = useTranslation('common');
  return (
    <div className={cn('flex flex-col items-center gap-3 p-8 text-center', className)} role="alert">
      <Illustration name="error.offline" fallbackGlyph="🌧️" size={96} />
      <p className="font-display text-md font-bold text-text">{t('states.errorTitle')}</p>
      <p className="text-sm text-text-muted">{t('states.errorBody')}</p>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t('states.retry')}
        </Button>
      )}
    </div>
  );
}

export function EmptyState({
  message,
  description,
  glyph = '🍃',
  art,
  action,
  className,
}: {
  message: string;
  /** Optional kind sub-line under the message (e.g. an encouraging example), so the state isn't bare. */
  description?: string;
  glyph?: string;
  /** A spot-illustration key (resolved from the icon pack, @/assets/icons); renders <Illustration> with `glyph` as the emoji fallback. */
  art?: string;
  /** Optional call-to-action (e.g. a Button/Link) so an empty state can lead somewhere, not dead-end. */
  action?: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <div className={cn('flex flex-col items-center gap-3 p-8 text-center', className)}>
      {art ? (
        <Illustration name={art} fallbackGlyph={glyph} size={96} />
      ) : (
        <span className="text-3xl" aria-hidden="true">
          {glyph}
        </span>
      )}
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium text-text-muted">{message}</p>
        {description && <p className="text-xs text-text-muted">{description}</p>}
      </div>
      {action}
    </div>
  );
}

/**
 * "You don't have permission" surface (ADR-0137) — for a query whose backing endpoint requires a
 * permission the caller's role may lack (e.g. a custom role missing `member:read`). Distinct from
 * {@link EmptyState}'s genuinely-empty case: the data isn't absent, the viewer just can't see it. Shares
 * `empty.locked`/🔒 with {@link FeatureDisabled} (`@/entities/session`) — both answer "why can't I use
 * this," just for a permission gap instead of a household feature toggle.
 */
export function PermissionDeniedState({
  action,
  className,
}: {
  /** Optional call-to-action (e.g. a link back), so this doesn't dead-end either. */
  action?: React.ReactNode;
  className?: string;
}): React.ReactElement {
  const { t } = useTranslation('common');
  return (
    <EmptyState
      art="empty.locked"
      glyph="🔒"
      message={t('states.forbiddenTitle')}
      description={t('states.forbiddenBody')}
      action={action}
      className={className}
    />
  );
}
